import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../api/client';
import './FinanceiroNerdz.css';

/**
 * Página Financeiro Nerdz - Saldo Caixinha Poker
 * Apenas para Admin
 */

const FinanceiroNerdz = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [saldoGeral, setSaldoGeral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editandoSaldoPassado, setEditandoSaldoPassado] = useState(false);
  const [novoSaldoPassado, setNovoSaldoPassado] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    carregarSaldoGeral();
  }, [isAdmin, navigate]);

  const carregarSaldoGeral = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get('/caixinha/geral');
      setSaldoGeral(response.data);
    } catch (err) {
      console.error('Erro ao carregar saldo geral:', err);
      setError(err.response?.data?.error || 'Erro ao carregar saldo geral');
    } finally {
      setLoading(false);
    }
  };

  const handleEditarSaldoPassado = () => {
    if (saldoGeral?.saldo_passado_editado) {
      alert('O saldo passado já foi editado e não pode ser alterado novamente. Use o script de correção se necessário.');
      return;
    }
    setNovoSaldoPassado(saldoGeral?.saldo_passado?.toFixed(2) || '0.00');
    setEditandoSaldoPassado(true);
  };

  const handleSalvarSaldoPassado = async (e) => {
    e.preventDefault();

    const valor = parseFloat(novoSaldoPassado);
    if (isNaN(valor) || valor < 0) {
      setError('Por favor, insira um valor válido (número positivo)');
      return;
    }

    try {
      setSalvando(true);
      setError('');
      setSuccess('');

      await apiClient.put('/caixinha/saldo-passado', {
        saldo_passado: valor,
      });

      setSuccess('Saldo passado atualizado com sucesso!');
      setEditandoSaldoPassado(false);
      await carregarSaldoGeral();
    } catch (err) {
      console.error('Erro ao salvar saldo passado:', err);
      setError(err.response?.data?.error || 'Erro ao salvar saldo passado');
    } finally {
      setSalvando(false);
    }
  };

  const handleCancelarEdicao = () => {
    setEditandoSaldoPassado(false);
    setNovoSaldoPassado('');
    setError('');
  };

  if (loading) {
    return <div className="container">Carregando...</div>;
  }

  if (!saldoGeral) {
    return (
      <div className="container">
        <div className="error">Erro ao carregar dados</div>
      </div>
    );
  }

  return (
    <div className="container financeiro-nerdz-container">
      <div className="page-header">
        <h1>💰 Financeiro Nerdz - Caixinha Poker</h1>
        <button onClick={() => navigate('/admin/menu')} className="btn btn-secondary">
          ← Voltar
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="caixinha-grid">
        {/* Saldo Passado */}
        <div className="caixinha-card">
          <h3>Saldo Passado (2025)</h3>
          {editandoSaldoPassado ? (
            <form onSubmit={handleSalvarSaldoPassado} className="form-edicao-saldo">
              <div className="form-group">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={novoSaldoPassado}
                  onChange={(e) => setNovoSaldoPassado(e.target.value)}
                  className="input-saldo"
                  placeholder="0.00"
                  required
                  autoFocus
                />
              </div>
              <div className="form-actions">
                <button type="submit" disabled={salvando} className="btn btn-primary btn-sm">
                  {salvando ? 'Salvando...' : 'Salvar'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelarEdicao}
                  disabled={salvando}
                  className="btn btn-secondary btn-sm"
                >
                  Cancelar
                </button>
              </div>
            </form>
          ) : (
            <>
              <p className="valor saldo-passado">
                R$ {parseFloat(saldoGeral.saldo_passado || 0).toFixed(2)}
              </p>
              {!saldoGeral.saldo_passado_editado && (
                <button
                  onClick={handleEditarSaldoPassado}
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: '10px' }}
                >
                  Editar Saldo Passado
                </button>
              )}
              {saldoGeral.saldo_passado_editado && (
                <small className="info-bloqueado">
                  (Editado - bloqueado para novas edições)
                </small>
              )}
            </>
          )}
        </div>

        {/* Saldo Total */}
        <div className="caixinha-card destaque">
          <h3>Saldo Total Caixinha</h3>
          <p className="valor saldo-total">
            R$ {parseFloat(saldoGeral.saldo_total || 0).toFixed(2)}
          </p>
          <small className="info">
            Saldo Passado + Caixinha Acumulada
          </small>
        </div>
      </div>

      {/* Caixinha Acumulada - por rodada */}
      <div className="caixinha-rodadas-card">
        <h3>Caixinha Acumulada</h3>
        <p className="valor caixinha-acumulada">
          R$ {parseFloat(saldoGeral.caixinha_acumulada || 0).toFixed(2)}
        </p>
        <small className="info">
          Soma das caixinhas de todas as rodadas fechadas
        </small>

        {saldoGeral.rodadas.length === 0 ? (
          <p className="no-data">Nenhuma rodada fechada ainda.</p>
        ) : (
          <table className="table-resultados">
            <thead>
              <tr>
                <th>Rodada</th>
                <th>Caixinha</th>
              </tr>
            </thead>
            <tbody>
              {saldoGeral.rodadas.map((r) => (
                <tr key={r.rodada_id}>
                  <td>{new Date(r.data).toLocaleDateString('pt-BR')}</td>
                  <td className={r.caixinha >= 0 ? 'saldo-positivo' : 'saldo-negativo'}>
                    R$ {parseFloat(r.caixinha).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="info-section">
        <h3>ℹ️ Informações</h3>
        <ul>
          <li><strong>Saldo Passado:</strong> Valor inicial da caixinha (ano 2025). Pode ser editado apenas uma vez.</li>
          <li><strong>Caixinha Acumulada:</strong> Soma automática das caixinhas de todas as rodadas fechadas, listada rodada a rodada (mais antiga primeiro).</li>
          <li><strong>Saldo Total:</strong> Soma do saldo passado com a caixinha acumulada.</li>
          <li><strong>Cálculo da Caixinha por Rodada:</strong> segue a configuração da tela "Lógica de Cálculo" (itens que vão direto pra caixinha + percentual sobre a base premiável, menos os gastos da rodada).</li>
        </ul>
      </div>
    </div>
  );
};

export default FinanceiroNerdz;
